import React, { useState } from 'react';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import config from '../lib/config';
import Head from 'next/head';
import Image from 'next/image';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { H2, Muted } from '../components/text';
import { Button } from '../components/button';
import { ITeam } from '../models/team';
import { IMenuItem } from '../models/menu-item';
import { ISponsor } from '../models/sponsor';
import { ITransfer } from '../models/transfer';
import { IPageDirect } from '../models/page';
import { classNames } from '../utils/applyCssClassNames';
import filterTeams from '../utils/filterHistoricalTeams';
import PostBody from '../components/post-body';
import { GetStaticProps } from 'next';

interface IProps {
	primaryMenuItems: IMenuItem[];
	pageData: { page: IPageDirect | null };
	sponsors: ISponsor[];
	transfers: ITransfer[];
	teams: ITeam[];
}

export default function TransferPage({ primaryMenuItems, pageData, sponsors, transfers, teams }: IProps) {
	const page = pageData?.page;
	const [images, setImages] = useState<ITransfer[]>(transfers);
	const [activeClub, setActiveClub] = useState<string>();

	const handleLogoClick = (logoName: string) => {
		const newActiveClub = activeClub === logoName ? undefined : logoName;
		setActiveClub(newActiveClub);

		const filteredImages = newActiveClub ? transfers.filter((image) => image.altText === logoName) : transfers;

		setImages(filteredImages);
	};

	const resetFilter = () => {
		setActiveClub(undefined);
		setImages(transfers);
	};

	const pageTitle = `${page?.title || 'Transfers'} | ${config.siteName}`;
	const meta = page?.seo?.description || page?.title || pageTitle;

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12 xl:py-16">
				<article>
					<H2 className="text-center py-6">{page?.title || 'Transfers'}</H2>
					<section className="mb-12  mx-auto lg:max-w-4xl">
						{page?.content && <PostBody content={page.content} />}
					</section>

					<section className="mb-12">
						<div className="py-4 flex w-full justify-end">
							<Button label="Toon alle" onClick={resetFilter} />
						</div>
						<div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-10 xl:grid-cols-10 gap-4">
							{teams.map((team) => {
								const className = classNames(
									'bg-white p-3 rounded-lg shadow-md flex justify-center flex-col items-center cursor-pointer hover:shadow-lg',
									'border-2 border-solid',
									activeClub === team.slug ? 'border-red-500' : '',
								);
								return (
									<div key={team.id} className={className} onClick={() => handleLogoClick(team.slug)}>
										<img
											className="w-16 select-none"
											src={team.team_logo}
											alt={team.title.rendered}
											data-logoname={team.slug}
										/>
									</div>
								);
							})}
						</div>
					</section>

					<section className="mb-12">
						{!images.length && (
							<div className="p-4 flex w-full justify-center">
								<Muted>Er zijn nu geen transfers voor deze club</Muted>
							</div>
						)}
						<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
							{images.map((transfer) => (
								<div className="mx-auto" key={transfer.id}>
									<Image
										alt={transfer.altText}
										src={transfer.sourceUrl}
										className="rounded-md"
										loading="lazy"
										width={267}
										height={267}
									/>
								</div>
							))}
						</div>
					</section>
				</article>
			</Container>
		</Layout>
	);
}

interface IApiProps {
	page: (IPageDirect & { transfers: { kaarten: ITransfer[] } }) | null;
	sponsors: { nodes: ISponsor[] };
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const pageData = await fetchAPI<IApiProps>(
		`
		query {
			page(id: 107872, idType: DATABASE_ID) {
				title
				content
				transfers {
					kaarten {
						altText
						id
						slug
						sourceUrl(size: MEDIUM)
						uri
						title
						date
					}
				}
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	// Fetch data for all the teams
	const response = await fetch(
		`${config.apiUrl}/wp-json/wp/v2/teams?per_page=40&order=asc&orderby=title&_fields=id,title,team_logo,slug`,
	);
	const teams: ITeam[] = await response.json();

	const filteredTeams = filterTeams(teams);

	const copyTransferCards = pageData.page ? [...pageData.page.transfers.kaarten] : [];
	const sortedTransferCards = copyTransferCards.sort(
		(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
	);

	return {
		revalidate: 3600,
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			transfers: sortedTransferCards,
			teams: filteredTeams,
		},
	};
};
